/**
 * The consumer hook. Overlays pending operations onto a document, and folds confirmed ones into the
 * caches it names.
 *
 * Structural operations are opt-in per connection (`connections`), because an insert position and
 * its count deltas are meaningful only in a document that actually renders that connection. Entity
 * `setFields` operations are not: an issue's fields can be edited from a side panel, a row, a
 * picker, or another page, so this hook matches them by identity wherever they appear rather than
 * making the document enumerate every id it might hold — an enumeration that silently misses edits
 * as soon as it falls behind the selection set.
 */
import {useQueryClient} from '@github-ui/react-query'
import {useEffect, useEffectEvent, useMemo} from 'react'

import {
  type ApplyOptions,
  applyOperations,
  collectIdentities,
  type ConnectionOperation,
  connectionPathKey,
  type PendingConnectionOperation,
} from './connection-operations'
import {settlementHandlers} from './settlement-registry'
import {
  type ConnectionMutationTarget,
  CONNECTION_MUTATION_KEY_ROOT,
  type ConnectionMutationVariables,
  connectionTargetKey,
  type SetFieldsMapper,
  projectSetFields,
  usePendingOperationEntries,
} from './pending-operations'
import {settleConfirmedOperation, type SettleTarget} from './settle-confirmed-operation'

export function useOptimisticData<T>(
  base: T,
  {
    identityPath,
    mapFields,
    connections,
    targets,
    enabled = true,
  }: {
    /** Where this document keeps the global id operations target. Defaults to `id`. */
    identityPath?: string[]
    /** Consumer-side field-name projection, e.g. a row payload's `titleHtml` for `titleHTML`. */
    mapFields?: SetFieldsMapper
    /** Connections this document renders, to also receive their structural operations. */
    connections?: ConnectionMutationTarget[]
    /**
     * The query keys this document renders from. Supply them to also receive **confirmed** values:
     * when a mutation succeeds, the server's fields are folded into these keys before the overlay
     * drops, so the value never flickers back through its pre-mutation state. Set `revalidate` on a
     * target to also refetch it, reconciling server-derived fields the operation doesn't carry
     * (timestamps, counts, pagination).
     *
     * Omit for a read-only view of someone else's data — the overlay still applies, but nothing is
     * written back.
     */
    targets?: SettleTarget[]
    enabled?: boolean
  } = {},
): T {
  const pendingEntries = usePendingOperationEntries()

  useSettlementRegistration({targets, connections, identityPath, mapFields, enabled})

  // Arrays and objects are new each render; compare by stable, collision-free keys so a caller
  // passing an inline `connections` array doesn't invalidate the overlay memo and re-walk the whole
  // document on every parent render. `mapFields` can't be normalised this way — callers must keep
  // it stable (a module-level function or a `useCallback`).
  const identityKey = identityPath ? connectionPathKey(identityPath) : undefined
  const connectionsKey = JSON.stringify(
    (connections ?? []).map(target => connectionTargetKey(target.ownerId, target.connectionPath)),
  )
  const connectionKeys = useMemo(() => new Set(JSON.parse(connectionsKey) as string[]), [connectionsKey])

  return useMemo(() => {
    // Before scanning: an idle page must not walk its document on every mutation-cache
    // notification, and most notifications belong to some other part of the page.
    if (!enabled || base == null || pendingEntries.length === 0) return base

    const applyOptions: ApplyOptions = identityKey ? {identityPath: JSON.parse(identityKey) as string[]} : {}

    // Index only when an entity operation is actually pending — a page with nothing but structural
    // operations never walks its document. Computed up front rather than lazily inside the loop:
    // a local reassigned from within a callback is untrackable for the React Compiler, which then
    // declines to optimize this hook at all, costing more than the walk it avoided.
    const identities = pendingEntries.some(entry => entry?.operation.type === 'setFields')
      ? collectIdentities(base, applyOptions.identityPath)
      : undefined

    const operations = pendingEntries.flatMap<PendingConnectionOperation>(entry => {
      if (entry == null) return []

      if (entry.operation.type !== 'setFields') {
        return connectionKeys.has(connectionTargetKey(entry.ownerId, entry.connectionPath)) ? [entry] : []
      }

      if (!identities?.has(entry.operation.id)) return []

      const operation = projectSetFields(entry.operation, mapFields)
      return operation ? [{...entry, operation}] : []
    })

    return operations.length === 0 ? base : applyOperations(base, operations, applyOptions)
    // `pendingEntries` is referentially stable across renders (via `replaceEqualDeep`).
  }, [enabled, base, pendingEntries, identityKey, mapFields, connectionKeys])
}

/**
 * Registers this document to receive confirmed values, so a successful mutation folds the server's
 * fields into `targets` *before* its overlay drops. Without it the overlay would drop onto a base
 * that never received the write, and the value would visibly revert until the next refetch.
 *
 * Internal to `useOptimisticData` on purpose: the overlay and the fold have to agree about
 * `identityPath`, `mapFields` and which connections this document renders, and a caller cannot get
 * that wrong if it cannot pass them separately.
 */
/** The registration a document is leaving, captured so cleanup describes it and not its successor. */
type RegisteredConfig = {
  targets: SettleTarget[]
  connections?: ConnectionMutationTarget[]
  identityPath?: string[]
}

function useSettlementRegistration({
  targets,
  connections,
  identityPath,
  mapFields,
  enabled,
}: {
  targets?: SettleTarget[]
  connections?: ConnectionMutationTarget[]
  identityPath?: string[]
  mapFields?: SetFieldsMapper
  enabled: boolean
}): void {
  const queryClient = useQueryClient()

  const handler = useEffectEvent(async (operation: ConnectionOperation, ownerId: string, connectionPath: string[]) => {
    if (!targets || targets.length === 0) return

    if (operation.type === 'setFields') {
      // Entity edits are judged by containment, which `settleConfirmedOperation` checks per
      // target — a document holding the entity settles, one that doesn't is skipped.
      const projected = projectSetFields(operation, mapFields)
      if (!projected) return
      await settleConfirmedOperation({
        queryClient,
        targets,
        // A `setFields` names its own target, so the owner is the entity itself.
        ownerId: operation.id,
        operation: projected,
        identityPath,
      })
      return
    }

    // Structural operations mean something only in a document that renders that exact
    // connection, which is the same list the overlay opts into.
    const rendersIt = (connections ?? []).some(
      target =>
        connectionTargetKey(target.ownerId, target.connectionPath) === connectionTargetKey(ownerId, connectionPath),
    )
    if (!rendersIt) return
    await settleConfirmedOperation({queryClient, targets, ownerId, operation, identityPath})
  })

  /**
   * A mutation still in flight when this document unregisters settles into a registry that no
   * longer holds it, so the fold that would have reconciled `base` never happens. Refresh instead
   * of leaving a still-optimistic base behind.
   *
   * Containment-filtered rather than blanket-invalidated: the entity channel is global, so every
   * field edit anywhere in the app is "outstanding" here, and unmounting during one would otherwise
   * cost this document a refetch it has no stake in.
   */
  const abandonOutstanding = useEffectEvent((outgoing: RegisteredConfig) => {
    const pending = queryClient
      .getMutationCache()
      .findAll({status: 'pending', mutationKey: [CONNECTION_MUTATION_KEY_ROOT]})
      .flatMap(mutation => {
        const operation = (mutation.state.variables as ConnectionMutationVariables | undefined)?.operation
        const key = mutation.options.mutationKey
        if (!operation || !Array.isArray(key)) return []
        return [{operation, ownerId: String(key[1]), connectionPath: Array.isArray(key[2]) ? (key[2] as string[]) : []}]
      })
    if (pending.length === 0) return

    // Structural operations are claimed by the connections this document renders; entity ones by
    // whether it holds the entity. Both read the *outgoing* configuration for the same reason the
    // targets do -- see the capture in the effect below.
    const rendersConnection = (ownerId: string, connectionPath: string[]) =>
      (outgoing.connections ?? []).some(
        target =>
          connectionTargetKey(target.ownerId, target.connectionPath) === connectionTargetKey(ownerId, connectionPath),
      )

    for (const target of outgoing.targets) {
      const base = queryClient.getQueryData(target.queryKey)
      if (base === undefined) continue
      const identities = collectIdentities(base, outgoing.identityPath)
      const mine = pending.some(entry =>
        entry.operation.type === 'setFields'
          ? identities.has(entry.operation.id)
          : rendersConnection(entry.ownerId, entry.connectionPath),
      )
      if (mine) void queryClient.invalidateQueries({queryKey: target.queryKey})
    }
  })

  const targetsKey = JSON.stringify((targets ?? []).map(target => target.queryKey))

  useEffect(() => {
    if (!enabled || !targets || targets.length === 0) return

    // Captured, because this cleanup runs after the next render has committed: an Effect Event
    // reads the *incoming* configuration, and abandonment has to describe the document that is
    // leaving. Targets decide which caches to refresh -- reading the new ones refreshes the caches
    // just registered and leaves the outgoing ones holding optimistic values nothing will reconcile.
    // Connections and identity decide *whether* a pending operation was this document's, and they
    // move with the targets: the issue viewer derives its connection owner from loaded data, so
    // mid-navigation it is `''` and would match nothing, silently skipping the outgoing cache.
    const registered: RegisteredConfig = {targets, connections, identityPath}

    settlementHandlers.add(handler)
    return () => {
      settlementHandlers.delete(handler)
      abandonOutstanding(registered)
    }
    // `handler` and `abandonOutstanding` are stable across renders (`useEffectEvent`); `targetsKey`
    // stands in for the array so an inline `targets` doesn't re-register every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, targetsKey])
}
